import type { FragmentType } from 'src/gql';
import { useState } from 'react';
import Link from 'next/link';
import { Button } from '@savvy-kit/components/button';
import Banner from '@savvy-kit/patterns/banner';
import { Megaphone } from 'lucide-react';
import { DISMISS_ANNOUNCEMENT } from 'src/constants/graphql/mutations/dismiss-announcement';
import { graphql, unmaskFragment } from 'src/gql';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

export const AdvisorDashboardAnnouncementsFragment = graphql(/* GraphQL */ `
  fragment AdvisorDashboardAnnouncements on CurrentUser {
    activeAnnouncements {
      id
      announcementName
    }
  }
`);

type Props = {
  readonly currentUserFrag: FragmentType<typeof AdvisorDashboardAnnouncementsFragment> | null | undefined;
};

export function AdvisorDashboardAnnouncements({ currentUserFrag }: Props) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  const [dismissAnnouncement] = useMutationWithErrorLogging(DISMISS_ANNOUNCEMENT, {
    refetchQueries: ['ClientsAdvisorInfo'],
  });

  const currentUser = unmaskFragment(AdvisorDashboardAnnouncementsFragment, currentUserFrag);
  const activeAnnouncements = currentUser?.activeAnnouncements ?? [];
  const visibleAnnouncements = activeAnnouncements.filter((a) => !dismissedIds.has(a.id));

  const handleDismiss = (announcementId: string) => {
    setDismissedIds((prev) => new Set(prev).add(announcementId));
    void dismissAnnouncement({
      variables: { input: { announcementConfigId: announcementId } },
    });
  };

  if (visibleAnnouncements.length === 0) return null;

  return (
    <>
      {visibleAnnouncements.map((announcement) => {
        return renderAnnouncementBanner(announcement.id, announcement.announcementName, () =>
          handleDismiss(announcement.id),
        );
      })}
    </>
  );
}

/** Banners! **/

function renderAnnouncementBanner(key: string, announcementName: string, onDismiss: () => void) {
  switch (announcementName) {
    case 'financial_planning_now_available':
      return (
        <Banner
          key={key}
          variant="info"
          className="mb-4"
          icon={<Megaphone />}
          title="Financial planning is now live in your clients tab! 🎉"
          description="We recommend attending a training before diving in. Sessions available March 3, 5, 10, and 12 at 1pm ET. Check your calendar for the invite."
          handleClickX={onDismiss}
        />
      );
    case 'tax_now_available':
      return (
        <Banner
          key={key}
          variant="info"
          className="mb-4"
          title="Tax filing is now available for your clients! 🇺🇸"
          description="Get deeper client context with integrated tax history and streamlined filing."
          actionButton={
            <Button variant="default" render={<Link href="/advisor/dashboard/savvy-tax" />}>
              Enroll clients
            </Button>
          }
          handleClickX={onDismiss}
        />
      );
    case 'tax_season_ending':
      return (
        <Banner
          key={key}
          variant="info"
          className="mb-4"
          title="Tax deadline: March 31st"
          description="Tax filing closes March 31st. Enroll your clients and submit all required documentation to qualify — or sign them up for an extension before the deadline."
          actionButton={
            <Button variant="default" render={<Link href="/advisor/dashboard/savvy-tax" />}>
              Review
            </Button>
          }
          handleClickX={onDismiss}
        />
      );
    case 'savvy_intelligence_launch':
      return (
        <Banner
          key={key}
          variant="info"
          className="mb-4"
          icon={<Megaphone />}
          title="Savvy Intelligence is live 🎉"
          description="Get started with your first planning scenario"
          actionButton={
            <Button variant="default" render={<Link href="/advisor/canvas" onClick={onDismiss} />}>
              Get started
            </Button>
          }
          handleClickX={onDismiss}
        />
      );
    default:
      return null;
  }
}
